import { TFunction } from 'react-i18next';
import { ASSIGNMENT_TYPES } from 'pages/AutomationEngine/dialogs/ActionWizard/subsections/ScheduleActivity/constants';
import DraggableStepCard, {
  DraggableStepCardProps,
} from '../../DraggableStepCard';
import type { ScheduleActivityStep } from 'pages/AutomationEngine/dialogs/ActionWizard/subsections/actionSectionTypes';
import type { DeletedOption } from 'pages/AutomationEngine/types';
import { getAssignToTypeOptions } from 'pages/AutomationEngine/dialogs/ActionWizard/subsections/ScheduleActivity/helpers';

type GetLabelOptions = {
  historyMode?: boolean;
  opt: (DeletedOption & { enabled?: boolean }) | null | undefined;
  t: TFunction;
};

const getLabel = (options: GetLabelOptions) => {
  const { opt, t, historyMode = false } = options;

  const postfix = {
    deleted: `[${t('Deleted')}]`,
    disabled: `[${t('Disabled')}]`,
  };

  const isDisabled = opt?.enabled === false;

  if (!opt?.label) {
    return postfix.deleted;
  }

  if (opt.deleted) {
    return `${opt.label} ${postfix.deleted}`;
  }

  if (historyMode && isDisabled) {
    return `${opt.label} ${postfix.disabled}`;
  }

  return opt.label;
};

export const content = ({ step }: { step: ScheduleActivityStep }) =>
  step.description;

const getAssignmentDescription = ({
  step,
  historyMode,
  t,
}: {
  step: ScheduleActivityStep;
  historyMode: boolean;
  t: TFunction;
}) => {
  const {
    assignedTo: { assignmentType, role, employee },
    activityType,
  } = step.details;

  const roleLabel = getLabel({ opt: role, historyMode, t });
  const activityTypeLabel = getLabel({ opt: activityType, historyMode, t });
  const employeeLabel = getLabel({ opt: employee, historyMode, t });

  const mapped = `Schedule Activity ‘${activityTypeLabel}’ to`;

  switch (assignmentType) {
    case ASSIGNMENT_TYPES.SA_TYPE_ROUND_ROBIN_ROLE:
      return `${mapped} Round Robin (${roleLabel} Role)`;
    case ASSIGNMENT_TYPES.SA_TYPE_LAST_ACTIVE_TEAM_MEMBER_SPEC_ROLE:
      return `${mapped} Last Active (${roleLabel} Role)`;
    case ASSIGNMENT_TYPES.SA_TYPE_LAST_ACTIVE_TEAM_MEMBER:
      return `${mapped} Last Active (Any Role)`;
    case ASSIGNMENT_TYPES.SA_TYPE_ROUND_ROBIN_TEAM_MEMBERS:
      return `${mapped} Round Robin (Specific Team)`;
    case ASSIGNMENT_TYPES.SA_TYPE_SPEC_ROLE:
      return `${mapped} ${roleLabel} Role`;
    case ASSIGNMENT_TYPES.SA_TYPE_TEAM_MEMBER:
      return `${mapped} ${employeeLabel}`;
    default:
      return `${mapped} ${getAssignToTypeOptions(t, true, false).find((o) => o.value === assignmentType)?.label || ''}`;
  }
};

export const getDescription = (options: {
  step: ScheduleActivityStep;
  historyMode: boolean;
  t: TFunction;
}) => {
  const { step, historyMode, t } = options;
  const description = getAssignmentDescription(options);
  const { targetVariable } = step.details;

  if (!targetVariable) {
    return description;
  }

  return `${description}. ${t('Activity id assigned to ‘{{variableName}}’', {
    variableName: getLabel({ opt: targetVariable, historyMode, t }),
  })}`;
};

export default function ScheduleActivityCard({
  step,
  ...others
}: {
  step: ScheduleActivityStep;
} & DraggableStepCardProps) {
  return <DraggableStepCard {...others}>{content({ step })}</DraggableStepCard>;
}
